import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react'
import Button from './Button'
import '../components/Modal.css'

interface ConfirmOptions {
  title: string
  message?: string
  confirmText?: string
  cancelText?: string
  /** Use danger styling for destructive actions (delete, disconnect, etc.) */
  danger?: boolean
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface DialogState extends ConfirmOptions {
  resolve: (value: boolean) => void
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descId = useId()

  const confirm = useCallback<ConfirmFn>(options => {
    return new Promise<boolean>(resolve => {
      setDialog({ ...options, resolve })
    })
  }, [])

  const close = useCallback(
    (result: boolean) => {
      setDialog(current => {
        current?.resolve(result)
        return null
      })
    },
    [],
  )

  useEffect(() => {
    if (!dialog) return

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null

    const focusFirst = () => {
      const focusable = contentRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (focusable && focusable.length > 0) {
        focusable[focusable.length - 1].focus()
      } else {
        contentRef.current?.focus()
      }
    }
    const timer = setTimeout(focusFirst, 0)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close(false)
        return
      }
      if (e.key !== 'Tab') return
      const focusable = contentRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (!focusable || focusable.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (e.shiftKey) {
        if (active === first || !contentRef.current?.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKey)
    return () => {
      clearTimeout(timer)
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKey)
      previouslyFocusedRef.current?.focus?.()
    }
  }, [dialog, close])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="modal-overlay" onClick={() => close(false)} role="presentation">
          <div
            ref={contentRef}
            className="modal-content"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={dialog.message ? descId : undefined}
            tabIndex={-1}
          >
            <h2 id={titleId} className="modal-title">{dialog.title}</h2>
            {dialog.message && <p id={descId} className="modal-message">{dialog.message}</p>}
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => close(false)}>
                {dialog.cancelText ?? 'キャンセル'}
              </Button>
              <Button variant={dialog.danger ? 'danger' : 'primary'} onClick={() => close(true)}>
                {dialog.confirmText ?? '確認'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return ctx
}
